import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataGrid, KeenIcon } from '@/components';
import { ConfirmationDialog } from '@/components/dialogBox';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';
import { promoValidationSchema } from '@/utils/validations';
import usePagination from '@/hooks/usePagination';
import { usePermissions } from '@/hooks/usePermissions';
import { ErrorApiResponse } from '@/types/api.types';
import {
  PromoListQueryParams,
  IPromoData,
  DiscountType,
} from '@/types/promo-management.types';
import {
  useDeletePromoMutation,
  useGetPromosQuery,
  useUpdatePromoMutation,
  useGetPromoByIdQuery,
} from '@/services/PromoManagementService';
import { PromoModal } from './PromoModal';
import { Statuses } from '@/constant';
import { formatDisplayDate } from '@/utils/Date';
import {
  formatFinnishCurrency,
  formatFinnishNumber,
  formatFinnishPercentage,
} from '@/utils';

const PromoListings = () => {
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
    handleSearch,
    searchTerm,
    pagination,
    setPagination,
    resetSearch,
  } = usePagination();
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    promo: IPromoData | null;
    mode: 'view' | 'edit' | 'create';
  }>({ isOpen: false, promo: null, mode: 'view' });
  const [confirmDeleteState, setConfirmDeleteState] = useState<{
    isOpen: boolean;
    promoId: string | null;
  }>({ isOpen: false, promoId: null });

  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [discountTypeFilter, setDiscountTypeFilter] = useState<string>('all');
  const [formData, setFormData] = useState<Partial<IPromoData>>({
    name: '',
    code: '',
    start_date: '',
    end_date: '',
    coupon_type: undefined,
    discount_amount: undefined,
    discount_percentage: undefined,
    max_discount: undefined,
    discount_type: DiscountType.FIXED_AMOUNT,
    min_order_value: undefined,
    total_quantity: undefined,
    remaining_usage_count: undefined,
    frequency: undefined,
    customer_usage_limit: undefined,
    description: '',
    is_restricted_to_groups: false,
    is_supplier_promotion: false,
    status: Statuses.ACTIVE,
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const { enqueueSnackbar } = useSnackbar();
  const [
    updatePromo,
    { isLoading: isUpdating, isSuccess: updateSuccess, isError: updateError },
  ] = useUpdatePromoMutation();
  const [deletePromo] = useDeletePromoMutation();

  const [promoIdToFetch, setPromoIdToFetch] = useState<string | null>(null);

  const queryParams: PromoListQueryParams = {
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: debouncedValue,
  };

  if (statusFilter !== 'all') {
    queryParams.status = parseInt(statusFilter) as Statuses;
  }

  if (discountTypeFilter !== 'all') {
    queryParams.discount_type = parseInt(discountTypeFilter) as DiscountType;
  }

  const {
    data: promosResponse,
    isLoading,
    isFetching,
    refetch,
  } = useGetPromosQuery(queryParams);

  useEffect(() => {
    if (typeof refetch === 'function') {
      refetch();
    }
  }, [debouncedValue, statusFilter, discountTypeFilter, refetch]);

  const {
    data: promoResponse,
    isSuccess: promoFetchSuccess,
    error: promoFetchError,
  } = useGetPromoByIdQuery(promoIdToFetch!, {
    skip: !promoIdToFetch,
  });

  useEffect(() => {
    if (promosResponse) {
      handleTotalPages(promosResponse.totalPages || 1);
      handleTotalItems(promosResponse.totalItems || 0);
    }
  }, [promosResponse, handleTotalPages, handleTotalItems]);

  useEffect(() => {
    if (promoFetchSuccess && promoResponse) {
      // promoResponse is already IPromoData from service transform
      setFormData(promoResponse);
      setErrors({});
      setModalState((prev) => ({
        ...prev,
        isOpen: true,
        promo: promoResponse,
      }));
      setPromoIdToFetch(null);
    }
  }, [promoFetchSuccess, promoResponse]);

  useEffect(() => {
    if (promoFetchError) {
      enqueueSnackbar('Failed to fetch promo details', { variant: 'error' });
    }
  }, [promoFetchError, enqueueSnackbar]);

  useEffect(() => {
    if (updateSuccess) {
      enqueueSnackbar('Promo code updated successfully', {
        variant: 'success',
      });
      handleCloseModal();
      refetch();
    }
  }, [updateSuccess, enqueueSnackbar, refetch]);

  useEffect(() => {
    if (updateError) {
      enqueueSnackbar('Failed to update promo code', { variant: 'error' });
    }
  }, [updateError, enqueueSnackbar]);

  const filteredData: IPromoData[] = useMemo(() => {
    return promosResponse?.data ?? [];
  }, [promosResponse]);

  const resetFilters = () => {
    if (searchTerm) resetSearch();
    setStatusFilter('all');
    setDiscountTypeFilter('all');
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const handleOpenModal = useCallback(
    (promo: IPromoData | null, mode: 'view' | 'edit' | 'create') => {
      if (mode === 'create') {
        setModalState({ isOpen: true, promo: null, mode: 'create' });
        setFormData({
          name: '',
          code: '',
          start_date: '',
          end_date: '',
          coupon_type: undefined,
          discount_amount: undefined,
          discount_percentage: undefined,
          max_discount: undefined,
          discount_type: DiscountType.FIXED_AMOUNT,
          min_order_value: undefined,
          total_quantity: undefined,
          remaining_usage_count: undefined,
          frequency: undefined,
          customer_usage_limit: undefined,
          description: '',
          is_restricted_to_groups: false,
          is_supplier_promotion: false,
          status: Statuses.ACTIVE,
        });
        setErrors({});
      } else if (promo) {
        setPromoIdToFetch(promo.id);
        setModalState({ isOpen: false, promo: null, mode });
      }
    },
    []
  );

  const handleCloseModal = () => {
    setModalState({ isOpen: false, promo: null, mode: 'view' });
    setFormData({});
    setErrors({});
  };

  const handleSubmit = async () => {
    try {
      await promoValidationSchema.validate(formData, { abortEarly: false });

      const payload = {
        ...formData,
        min_order_value: Number(formData.min_order_value),
        discount_amount: formData.discount_amount
          ? Number(formData.discount_amount)
          : undefined,
        discount_percentage: formData.discount_percentage
          ? Number(formData.discount_percentage)
          : undefined,
        max_discount: formData.max_discount
          ? Number(formData.max_discount)
          : undefined,
        total_quantity: formData.total_quantity
          ? Number(formData.total_quantity)
          : undefined,
        remaining_usage_count: formData.remaining_usage_count
          ? Number(formData.remaining_usage_count)
          : undefined,
        customer_usage_limit: formData.customer_usage_limit
          ? Number(formData.customer_usage_limit)
          : undefined,
      };

      if (modalState.mode === 'edit' && modalState.promo?.id) {
        await updatePromo({
          promo_id: modalState.promo.id,
          data: payload,
        }).unwrap();
      }
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const validationErrors: { [key: string]: string } = {};
        err.inner.forEach((error) => {
          if (error.path) validationErrors[error.path] = error.message;
        });
        setErrors(validationErrors);
        enqueueSnackbar(
          `Please fix the form errors (${err.inner.length} errors occurred)`,
          { variant: 'error' }
        );
      } else {
        const errorMessage =
          (err as ErrorApiResponse).data?.message ||
          'Failed to update promo code';
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    }
  };

  const handleDelete = async () => {
    if (!confirmDeleteState.promoId) return;
    try {
      await deletePromo(confirmDeleteState.promoId).unwrap();
      enqueueSnackbar('Promo code deleted successfully', {
        variant: 'success',
      });
      setConfirmDeleteState({ isOpen: false, promoId: null });
      refetch();
    } catch (err) {
      enqueueSnackbar(
        (err as ErrorApiResponse).data?.message ||
          'Failed to delete promo code',
        { variant: 'error' }
      );
      setConfirmDeleteState({ isOpen: false, promoId: null });
    }
  };

  const getDiscountTypeLabel = (type: DiscountType) => {
    switch (type) {
      case DiscountType.PERCENTAGE:
        return 'Percentage';
      case DiscountType.FIXED_AMOUNT:
        return 'Fixed Amount';
      default:
        return 'Unknown';
    }
  };

  const getStatusLabel = (status: Statuses) => {
    switch (status) {
      case Statuses.ACTIVE:
        return 'Active';
      case Statuses.INACTIVE:
        return 'Inactive';
      case Statuses.DELETED:
        return 'Deleted';
      case Statuses.BLOCKED:
        return 'Blocked';
      default:
        return 'Unknown';
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorFn: (row: IPromoData) => row.name,
        id: 'name',
        header: 'Promo Code',
        enableSorting: false,
        cell: ({ row }: { row: { original: IPromoData } }) => (
          <button
            type="button"
            onClick={() => handleOpenModal(row.original, 'view')}
            className="flex flex-col items-start text-left focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-sm"
            aria-label={`View ${row.original.name}`}
          >
            <span className="text-sm font-medium text-gray-900 hover:text-primary-active">
              {row.original.name}
            </span>
            <span className="text-xs text-gray-500">{row.original.code}</span>
          </button>
        ),
        meta: { className: 'min-w-[220px]' },
      },
      {
        accessorFn: (row: IPromoData) => row.discount_type,
        id: 'discount',
        header: 'Discount',
        enableSorting: false,
        cell: ({ row }: { row: { original: IPromoData } }) => (
          <div className="flex flex-col">
            <span className="text-sm font-medium text-gray-900">
              {getDiscountTypeLabel(row.original.discount_type)}
            </span>
            <span className="text-xs text-gray-500">
              {row.original.discount_type === DiscountType.PERCENTAGE
                ? formatFinnishPercentage(row.original.discount_percentage ?? 0)
                : formatFinnishCurrency(row.original.discount_amount ?? 0)}
            </span>
          </div>
        ),
        meta: { className: 'min-w-[160px]' },
      },
      {
        accessorFn: (row: IPromoData) => row.start_date,
        id: 'validity',
        header: 'Validity Period',
        enableSorting: false,
        cell: ({ row }: { row: { original: IPromoData } }) => (
          <div className="flex flex-col">
            <span className="text-xs text-gray-500">
              From: {formatDisplayDate(new Date(row.original.start_date))}
            </span>
            <span className="text-xs text-gray-500">
              To: {formatDisplayDate(new Date(row.original.end_date))}
            </span>
          </div>
        ),
        meta: { className: 'min-w-[180px]' },
      },
      {
        accessorFn: (row: IPromoData) => row.total_quantity,
        id: 'usage',
        header: 'Usage',
        enableSorting: false,
        cell: ({ row }: { row: { original: IPromoData } }) => (
          <div className="flex flex-col">
            <span className="text-sm text-gray-900">
              {formatFinnishNumber(row.original.remaining_usage_count ?? 0, {
                maximumFractionDigits: 0,
              })}{' '}
              /{' '}
              {row.original.total_quantity !== undefined &&
              row.original.total_quantity !== null
                ? formatFinnishNumber(row.original.total_quantity, {
                    maximumFractionDigits: 0,
                  })
                : '∞'}
            </span>
            <span className="text-xs text-gray-500">Remaining</span>
          </div>
        ),
        meta: { className: 'min-w-[140px]' },
      },
      {
        accessorFn: (row: IPromoData) => row.status,
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }: { row: { original: IPromoData } }) => {
          const statusClassMap: Record<Statuses, string> = {
            [Statuses.ACTIVE]: 'bg-green-100 text-green-800',
            [Statuses.INACTIVE]: 'bg-red-100 text-red-800',
            [Statuses.DELETED]: 'bg-gray-100 text-gray-800',
            [Statuses.BLOCKED]: 'bg-yellow-100 text-yellow-800',
            [Statuses.UNDER_RENOVATION]: 'bg-blue-100 text-blue-800',
          };

          return (
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                statusClassMap[row.original.status] ??
                'bg-gray-100 text-gray-800'
              }`}
            >
              {getStatusLabel(row.original.status)}
            </span>
          );
        },
        meta: { className: 'min-w-[120px]' },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }: { row: { original: IPromoData } }) => (
          <div className="flex items-center gap-2">
            {hasPermission('coupon_management_read') && (
              <button
                type="button"
                onClick={() => handleOpenModal(row.original, 'view')}
                className="btn btn-sm btn-icon btn-light btn-outline"
                title="View"
                aria-label={`View ${row.original.name}`}
              >
                <KeenIcon type="filled" icon="eye" className="text-gray-500" />
              </button>
            )}
            {hasPermission('coupon_management_update') && (
              <button
                type="button"
                onClick={() =>
                  navigate(`/promo-management/${row.original.id}/edit`)
                }
                className="btn btn-sm btn-icon btn-light btn-outline"
                title="Edit"
                aria-label={`Edit ${row.original.name}`}
              >
                <KeenIcon
                  type="filled"
                  icon="pencil"
                  className="text-gray-500"
                />
              </button>
            )}
            {hasPermission('coupon_management_delete') && (
              <button
                type="button"
                onClick={() =>
                  setConfirmDeleteState({
                    isOpen: true,
                    promoId: row.original.id,
                  })
                }
                className="btn btn-sm btn-icon btn-light btn-outline"
                title="Delete"
                aria-label={`Delete ${row.original.name}`}
              >
                <KeenIcon type="filled" icon="trash" className="text-red-500" />
              </button>
            )}
          </div>
        ),
        meta: { className: 'w-[120px]' },
      },
    ],
    [handleOpenModal, hasPermission, navigate]
  );

  return (
    <div className="promo-listings card card-grid w-full">
      <div className="card-header flex flex-wrap items-center gap-2 sm:gap-4 lg:gap-6 justify-between">
        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
          <div className="flex-shrink-0" role="search">
            <label
              className="input input-sm h-8 sm:h-10 flex items-center"
              htmlFor="search-promo-codes"
              aria-label="Search promo codes"
            >
              <KeenIcon
                type="filled"
                icon="magnifier"
                className="text-gray-500"
                aria-hidden="true"
              />
              <input
                id="search-promo-codes"
                placeholder="Search promo codes"
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                className="w-full sm:w-48 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                aria-label="Search promo codes by name or code"
                disabled={isLoading || isFetching}
              />
            </label>
          </div>
          <div className="flex-shrink-0">
            <select
              id="promo-status-filter"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              }}
              className="select select-sm h-8 sm:h-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 pr-[30px]"
              aria-label="Filter promo codes by status"
              disabled={isLoading || isFetching}
            >
              <option value="all">All Statuses</option>
              <option value={Statuses.ACTIVE}>Active</option>
              <option value={Statuses.INACTIVE}>Inactive</option>
              <option value={Statuses.BLOCKED}>Blocked</option>
              <option value={Statuses.DELETED}>Deleted</option>
            </select>
          </div>
          <div className="flex-shrink-0">
            <select
              id="promo-discount-type-filter"
              value={discountTypeFilter}
              onChange={(e) => {
                setDiscountTypeFilter(e.target.value);
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              }}
              className="select select-sm h-8 sm:h-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 pr-[30px]"
              aria-label="Filter promo codes by discount type"
              disabled={isLoading || isFetching}
            >
              <option value="all">All Discount Types</option>
              <option value={DiscountType.FIXED_AMOUNT}>Fixed Amount</option>
              <option value={DiscountType.PERCENTAGE}>Percentage</option>
            </select>
          </div>
        </div>
        <div
          className="flex items-center gap-2"
          role="toolbar"
          aria-label="Filter controls"
        >
          {(searchTerm ||
            statusFilter !== 'all' ||
            discountTypeFilter !== 'all') && (
            <button
              type="button"
              className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
              onClick={resetFilters}
              aria-label="Clear all filters"
              title="Clear all filters"
              disabled={isLoading || isFetching}
            >
              <KeenIcon
                type="solid"
                icon="arrows-circle"
                className="text-gray-600"
                aria-hidden="true"
              />
              Clear Filters
            </button>
          )}
        </div>
      </div>
      <div className="card-body">
        {isLoading || isFetching ? (
          <div
            className="text-center py-4"
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <KeenIcon
              type="filled"
              icon="spinner"
              className="text-gray-500 text-2xl animate-spin"
              aria-hidden="true"
            />
            <span className="ml-2 text-gray-700">Loading...</span>
          </div>
        ) : (
          <>
            {!filteredData.length && (
              <div className="text-center py-4">
                <p className="text-gray-500">No promo codes available</p>
              </div>
            )}
            <DataGrid
              columns={columns}
              data={filteredData}
              rowSelect={false}
              loading={isLoading || isFetching}
              pagination={{
                page: pagination.pageIndex,
                size: pagination.pageSize,
                more: true,
                totalItems,
                totalPages,
                setPagination,
                info: '{from} - {to} of {count}',
                sizes: [5, 10, 25, 50, 100],
                sizesLabel: 'Show',
                sizesDescription: 'per page',
              }}
              sorting={[{ id: 'name', desc: false }]}
              serverSide={true}
              onFilterReset={resetFilters}
              hasActiveFilters={
                !!searchTerm ||
                statusFilter !== 'all' ||
                discountTypeFilter !== 'all'
              }
              aria-label="Coupon codes listings table"
            />
          </>
        )}
      </div>

      <PromoModal
        open={modalState.isOpen}
        onClose={handleCloseModal}
        mode={modalState.mode}
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        setErrors={setErrors}
        onSubmit={handleSubmit}
        isSubmitting={isUpdating}
      />

      <ConfirmationDialog
        open={confirmDeleteState.isOpen}
        onClose={() => setConfirmDeleteState({ isOpen: false, promoId: null })}
        onConfirm={handleDelete}
        message="Are you sure you want to delete this coupon code? This action cannot be undone."
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
        confirmButtonVariant="destructive"
      />
    </div>
  );
};

export { PromoListings };
